import { RadioButtonGroup } from "@mixitone/components";
import { Panel } from "@mixitone/components/Controls";
import ThumbsDownIcon from "@mixitone/icons/thumbs-down.svg";
import ThumbsUpIcon from "@mixitone/icons/thumbs-up.svg";
import { ApplicationView } from "@mixitone/mvc";
import { ClubDetailsPageController } from "controllers/ClubDetailsPageController";
import { useMemo } from "react";
import { formatNextSessionHeading } from "./clubDetailsFormatting";

export const NextSessionPanel = ApplicationView(() => {
  const controller = ClubDetailsPageController.use();
  const { sessions, sessionIntents, loadingSessionIntents, savingSessionIntent, nextSession } =
    controller.state;

  const nextSessionIntent = nextSession
    ? (sessionIntents.find(
        (row) =>
          row.recurringSessionId === nextSession.recurringSessionId &&
          row.sessionDate === nextSession.localDate,
      ) ?? sessionIntents.find((row) => row.recurringSessionId === nextSession.recurringSessionId))
    : null;
  const rsvpBusy = loadingSessionIntents || savingSessionIntent;
  const nextSessionRsvpOptions = useMemo(
    () => [
      {
        value: "yes",
        label: "I'll be there",
        selectedKind: "accent" as const,
        labelClassName: "text-sm",
        icon: <ThumbsUpIcon className="size-6 shrink-0" aria-hidden />,
      },
      {
        value: "no",
        label: "No",
        selectedKind: "danger" as const,
        labelClassName: "text-sm",
        icon: <ThumbsDownIcon className="size-6 shrink-0" aria-hidden />,
      },
    ],
    [],
  );

  if (sessions.length === 0) {
    return null;
  }

  return (
    <Panel title="Next session" titleClassName="font-semibold">
      {nextSession ? (
        <div className="mb-4 flex flex-col gap-3">
          <div className="text-xl font-semibold">
            {formatNextSessionHeading(
              nextSession,
              typeof globalThis !== "undefined" && globalThis.navigator?.language
                ? globalThis.navigator.language
                : "en-NZ",
            )}
          </div>
          {loadingSessionIntents ? (
            <div aria-busy="true">
              <RadioButtonGroup
                name="next-session-rsvp-loading"
                variant="button"
                value=""
                options={nextSessionRsvpOptions}
                disabled
                onChange={() => {}}
              />
            </div>
          ) : !nextSessionIntent ? (
            <p className="text-sm text-slate-600">
              RSVP options could not be loaded. Refresh the page and try again.
            </p>
          ) : !nextSessionIntent.sessionDate ? (
            <p className="text-sm text-slate-600">
              There isn&apos;t an upcoming session instance to RSVP for yet.
            </p>
          ) : (
            <div
              aria-busy={savingSessionIntent}
              aria-label={
                !nextSessionIntent.canEdit
                  ? nextSessionIntent.attending === true
                    ? "Your response: I'll be there"
                    : nextSessionIntent.attending === false
                      ? "Your response: not attending"
                      : "No response saved for this session"
                  : undefined
              }
            >
              <RadioButtonGroup
                name={`next-session-rsvp-${nextSessionIntent.recurringSessionId}-${nextSessionIntent.sessionDate ?? ""}`}
                variant="button"
                value={
                  nextSessionIntent.attending === true
                    ? "yes"
                    : nextSessionIntent.attending === false
                      ? "no"
                      : ""
                }
                options={nextSessionRsvpOptions}
                disabled={!nextSessionIntent.canEdit || rsvpBusy}
                onChange={(e) => {
                  const v = String(e.target.value);
                  if (v === "yes") {
                    void controller.actionSetSessionIntent(nextSessionIntent.recurringSessionId, true);
                  } else if (v === "no") {
                    void controller.actionSetSessionIntent(nextSessionIntent.recurringSessionId, false);
                  }
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 text-sm opacity-80">No upcoming sessions found.</div>
      )}
    </Panel>
  );
});
